import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TextField, Typography } from "@mui/material";
import { Controller, useForm, FormProvider } from "react-hook-form";
import useFormFactoryLogic from "@/modules/lead-page/hooks/lead-info/status/form-factory/form-factory-logic.hook";
import { FormFactoryProps } from "@/modules/common/types";
import { StatusFormSubmitAction } from "@/modules/lead-page/types";
import { StatusType } from "@/modules/common/dtos/index.v2";
import FormItemRenderer from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/form-field-renderer";
import PropertyTrackUnitField from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/property-track-unit-field";
import FormFooter from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-footer";
import Grid from "@mui/material/GridLegacy";

export const FormFactory: React.FC<FormFactoryProps> = ({
  submitFun,
  formFields,
  isLoadingSubmition,
  handleClose,
  selectedStatus,
  showComment = true,
  hideFooter = false,
  onReady,
  onSaveAndCreateTracks,
}) => {
  const defaultValues = React.useMemo(() => {
    const values: Record<string, any> = {};
    formFields?.fields?.forEach((field) => {
      const fieldName = field.field.key || field.field.uid;

      if (field.field.defaultValue) {
        if (field.field.type === "dropdown") {
          const defaultValueOption = field.field.dropdowns.find(
            (option) => option.value === field.field.defaultValue
          );
          values[fieldName] = field.isMulti
            ? [
                {
                  label: defaultValueOption.label,
                  value: defaultValueOption.value,
                },
              ]
            : {
                label: defaultValueOption.label,
                value: defaultValueOption.value,
              };
        } else {
          values[fieldName] = field.field.defaultValue;
        }
      } else {
        values[fieldName] = field.isMulti ? [] : null;
      }
    });
    return values;
  }, [formFields]);

  const methods = useForm({
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const { onSubmit, hasCommentTitle } = useFormFactoryLogic({
    submitFun,
    errors,
  });


  const [submitAction, setSubmitAction] =
    useState<StatusFormSubmitAction>("save");


  const showSaveAndCreateTracks = Boolean(
    !hideFooter && onSaveAndCreateTracks && selectedStatus?.createDeveloperTrack
  );

  const handleFormSubmit = handleSubmit((data) => {
    setSubmitAction("save");
    onSubmit(data);
  });

  const handleSaveAndCreateTracks = handleSubmit((data) => {
    setSubmitAction("saveAndCreateTracks");
    onSaveAndCreateTracks?.(data);
  });

  const getValidatedValues = useCallback(() => {
    return new Promise<Record<string, any> | null>((resolve) => {
      handleSubmit(
        (data) => resolve(data),
        () => resolve(null)
      )();
    });
  }, [handleSubmit]);

  useEffect(() => {
    onReady?.({ getValidatedValues });
  }, [onReady, getValidatedValues]);

  const isNoAnswerStatus =
    selectedStatus?.statusType === StatusType.NO_ANSWER;

  const sortedFields = useMemo(
    () =>
      formFields?.fields?.sort(
        (a, b) =>
          new Date(a.createdAt || "").getTime() -
          new Date(b.createdAt || "").getTime()
      ),
    [formFields?.fields]
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmit}>
        <Typography variant="h5" gutterBottom>
          {formFields?.name}
        </Typography>
        <Grid container spacing={2} sx={{ paddingTop: "8px" }}>
          {sortedFields?.map((field) => (
            <React.Fragment key={field.id}>
              <FormItemRenderer
                field={field}
                control={control}
                watch={watch}
                setValue={setValue}
              />
            </React.Fragment>
          ))}

          {/* Property Tracks input driven by the status's
              showPropertyTrackUnit flag — no form field needed. */}
          {selectedStatus?.showPropertyTrackUnit && (
            <Grid item mobile={12}>
              <PropertyTrackUnitField control={control} setValue={setValue} />
            </Grid>
          )}

          {showComment && !hasCommentTitle(formFields) && (
            <Grid item mobile={12}>
              <Controller
                name="comment"
                control={control}
                rules={{
                  required: isNoAnswerStatus
                    ? false
                    : "This field is required",
                  minLength: isNoAnswerStatus
                    ? undefined
                    : {
                        value: 4,
                        message: "Minimum 4 characters required",
                      },
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Comment"
                    multiline
                    rows={4}
                    value={value || ""}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ "data-testid": "status-form-comment" }}
                  />
                )}
              />
            </Grid>
          )}
          {!hideFooter && (
            <FormFooter
              handleClose={handleClose}
              isLoadingSubmition={isLoadingSubmition}
              confirmText="Save"
              extraAction={
                showSaveAndCreateTracks
                  ? {
                      text: "Save & Create Tracks",
                      onClick: handleSaveAndCreateTracks,
                      isSubmitting: submitAction === "saveAndCreateTracks",
                    }
                  : undefined
              }
            />
          )}
        </Grid>
      </form>
    </FormProvider>
  );
};
